import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { X, Lightbulb } from 'lucide-react'
import { learningApi, type CoachNote } from '@/api/learning'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { formatCoachNoteCta, formatCoachNoteMessage } from '@/i18n/formatCoachNote'
import { useMinCopy } from '@/i18n/useUiLocale'

interface CoachNoteCardProps {
  coachNote: CoachNote | null
}

export function CoachNoteCard({ coachNote }: CoachNoteCardProps) {
  const { coach } = useMinCopy()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const dismissMutation = useMutation({
    mutationFn: (id: string) => learningApi.dismissInsight(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  if (!coachNote) {
    return null
  }

  const message = formatCoachNoteMessage(coachNote, coach)
  const ctaLabel = formatCoachNoteCta(coachNote.ctaAction, coach, coachNote.ctaLabel)

  const handleCta = (note: CoachNote) => {
    if (note.ctaAction === 'PRACTICE') {
      navigate('/reading')
      return
    }
    if (note.ctaAction === 'CHALLENGE') {
      navigate('/tests')
      return
    }
    if (note.focusUserWordId) {
      navigate(`/vocabulary/${note.focusUserWordId}`)
    }
  }

  return (
    <Card
      className="border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-card to-card p-5"
      data-testid="coach-note-card"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <Lightbulb className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              {coach.title}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-foreground">{message}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:pt-1">
          <Button
            className="h-9 w-auto px-4 text-sm"
            loading={dismissMutation.isPending}
            onClick={() => handleCta(coachNote)}
          >
            {ctaLabel}
          </Button>
          <button
            type="button"
            onClick={() => dismissMutation.mutate(coachNote.id)}
            disabled={dismissMutation.isPending}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={coach.dismiss}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Card>
  )
}
